import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toShortId } from '../utils/base62';

/**
 * Custom hook that syncs view state with URL navigation
 * This bridges the gap between the old setView() system and React Router
 *
 * Now supports dynamic routes with shortened IDs:
 * - /player/:shortId/dashboard
 * - /team/:shortId/dashboard
 *
 * IDs are compressed from 20 chars to ~12 chars for cleaner URLs
 *
 * Usage in App.js:
 * const navigate = useViewNavigation(view, setView, activePlayerId, activeSeasonId, activeTeamId);
 */
export function useViewNavigation(view, setView, activePlayerId, activeSeasonId, activeTeamId) {
  const navigate = useNavigate();
  const location = useLocation();

  // Generate dynamic URLs based on current state
  const getPathForView = (viewName) => {
    const routes = {
      'landing': '/',
      'dashboard': '/dashboard',
      'player-dashboard': activePlayerId ? `/player/${toShortId(activePlayerId)}` : '/player-dashboard',
      'player-setup': '/player/new',
      'season-setup': activePlayerId ? `/player/${toShortId(activePlayerId)}/season/new` : '/season-setup',
      'season-edit': activePlayerId && activeSeasonId ? `/player/${toShortId(activePlayerId)}/season/${toShortId(activeSeasonId)}/edit` : '/season-edit',
      'season-detail': activePlayerId && activeSeasonId ? `/player/${toShortId(activePlayerId)}/season/${toShortId(activeSeasonId)}` : '/season-detail',
      'match-history': activePlayerId && activeSeasonId ? `/player/${toShortId(activePlayerId)}/season/${toShortId(activeSeasonId)}/matches` : '/match-history',
      'record': activePlayerId && activeSeasonId ? `/player/${toShortId(activePlayerId)}/season/${toShortId(activeSeasonId)}/record` : '/record',
      'trends': activePlayerId ? `/player/${toShortId(activePlayerId)}/trends` : '/trends',
      'tournament-create': '/tournament/new',
      'tournament-edit': '/tournament/edit',
      'tournament-detail': '/tournament/detail',
      'shared-player-stats': '/shared-player-stats',
      'leaderboard': '/leaderboard',
      'pricing': '/plans',
      'privacy': '/privacy',
      'changelog': '/changelog',
      'contact': '/contact',
      'blog-admin': '/blog-admin',
      'account': '/account',
      // Team routes
      'teams': '/teams',
      'team-setup': '/team/new',
      'team-dashboard': activeTeamId ? `/team/${toShortId(activeTeamId)}` : '/team-dashboard',
      'team-roster': activeTeamId ? `/team/${toShortId(activeTeamId)}/roster` : '/team-roster',
      'team-seasons': activeTeamId ? `/team/${toShortId(activeTeamId)}/seasons` : '/team-seasons',
      'team-season-detail': activeTeamId ? `/team/${toShortId(activeTeamId)}/season/detail` : '/team-season-detail',
      'team-tournaments': activeTeamId ? `/team/${toShortId(activeTeamId)}/tournaments` : '/team-tournaments',
      'team-tournament-detail': activeTeamId ? `/team/${toShortId(activeTeamId)}/tournament/detail` : '/team-tournament-detail',
      'team-record-match': activeTeamId ? `/team/${toShortId(activeTeamId)}/record` : '/team-record-match',
      'team-stats': activeTeamId ? `/team/${toShortId(activeTeamId)}/stats` : '/team-stats',
      'team-season-stats': activeTeamId ? `/team/${toShortId(activeTeamId)}/season/stats` : '/team-season-stats',
      'team-reports': activeTeamId ? `/team/${toShortId(activeTeamId)}/reports` : '/team-reports',
      'team-comparison': activeTeamId ? `/team/${toShortId(activeTeamId)}/comparison` : '/team-comparison',
      'team-player-progress': activeTeamId ? `/team/${toShortId(activeTeamId)}/player-progress` : '/team-player-progress',
      'team-player-report-cards': activeTeamId ? `/team/${toShortId(activeTeamId)}/report-cards` : '/team-player-report-cards',
      'team-lineups': activeTeamId ? `/team/${toShortId(activeTeamId)}/lineups` : '/team-lineups',
      'team-lineup-editor': activeTeamId ? `/team/${toShortId(activeTeamId)}/lineup/edit` : '/team-lineup-editor',
      'team-work-list': activeTeamId ? `/team/${toShortId(activeTeamId)}/work-list` : '/team-work-list',
      'team-schedule': activeTeamId ? `/team/${toShortId(activeTeamId)}/schedule` : '/team-schedule'
    };
    return routes[viewName] || `/${viewName}`;
  };

  // Parse view name from dynamic URL
  const getViewFromPath = (pathname) => {
    // Match dynamic routes (check specific routes BEFORE generic ones)
    if (pathname.match(/^\/player\/new$/)) return 'player-setup';
    if (pathname.match(/^\/player\/[^/]+$/)) return 'player-dashboard';
    if (pathname.match(/^\/player\/[^/]+\/season\/new$/)) return 'season-setup';
    if (pathname.match(/^\/player\/[^/]+\/season\/[^/]+\/edit$/)) return 'season-edit';
    if (pathname.match(/^\/player\/[^/]+\/season\/[^/]+$/)) return 'season-detail';
    if (pathname.match(/^\/player\/[^/]+\/season\/[^/]+\/matches$/)) return 'match-history';
    if (pathname.match(/^\/player\/[^/]+\/season\/[^/]+\/record$/)) return 'record';
    if (pathname.match(/^\/player\/[^/]+\/trends$/)) return 'trends';

    if (pathname.match(/^\/team\/new$/)) return 'team-setup';
    if (pathname.match(/^\/team\/[^/]+$/)) return 'team-dashboard';
    if (pathname.match(/^\/team\/[^/]+\/roster$/)) return 'team-roster';
    if (pathname.match(/^\/team\/[^/]+\/seasons$/)) return 'team-seasons';
    if (pathname.match(/^\/team\/[^/]+\/season\/detail$/)) return 'team-season-detail';
    if (pathname.match(/^\/team\/[^/]+\/tournaments$/)) return 'team-tournaments';
    if (pathname.match(/^\/team\/[^/]+\/tournament\/detail$/)) return 'team-tournament-detail';
    if (pathname.match(/^\/team\/[^/]+\/record$/)) return 'team-record-match';
    if (pathname.match(/^\/team\/[^/]+\/stats$/)) return 'team-stats';
    if (pathname.match(/^\/team\/[^/]+\/season\/stats$/)) return 'team-season-stats';
    if (pathname.match(/^\/team\/[^/]+\/reports$/)) return 'team-reports';
    if (pathname.match(/^\/team\/[^/]+\/comparison$/)) return 'team-comparison';
    if (pathname.match(/^\/team\/[^/]+\/player-progress$/)) return 'team-player-progress';
    if (pathname.match(/^\/team\/[^/]+\/report-cards$/)) return 'team-player-report-cards';
    if (pathname.match(/^\/team\/[^/]+\/lineups$/)) return 'team-lineups';
    if (pathname.match(/^\/team\/[^/]+\/lineup\/edit$/)) return 'team-lineup-editor';
    if (pathname.match(/^\/team\/[^/]+\/work-list$/)) return 'team-work-list';
    if (pathname.match(/^\/team\/[^/]+\/schedule$/)) return 'team-schedule';

    // Static routes
    const staticRoutes = {
      '/': 'landing',
      '/dashboard': 'dashboard',
      '/player-dashboard': 'player-dashboard',
      '/season-setup': 'season-setup',
      '/season-edit': 'season-edit',
      '/season-detail': 'season-detail',
      '/match-history': 'match-history',
      '/record': 'record',
      '/trends': 'trends',
      '/tournament/new': 'tournament-create',
      '/tournament/edit': 'tournament-edit',
      '/tournament/detail': 'tournament-detail',
      '/shared-player-stats': 'shared-player-stats',
      '/leaderboard': 'leaderboard',
      '/plans': 'pricing',
      '/privacy': 'privacy',
      '/changelog': 'changelog',
      '/contact': 'contact',
      '/blog-admin': 'blog-admin',
      '/account': 'account',
      '/teams': 'teams',
      '/team-dashboard': 'team-dashboard',
      '/team-roster': 'team-roster',
      '/team-seasons': 'team-seasons',
      '/team-season-detail': 'team-season-detail',
      '/team-tournaments': 'team-tournaments',
      '/team-tournament-detail': 'team-tournament-detail',
      '/team-record-match': 'team-record-match',
      '/team-stats': 'team-stats',
      '/team-season-stats': 'team-season-stats',
      '/team-reports': 'team-reports',
      '/team-comparison': 'team-comparison',
      '/team-player-progress': 'team-player-progress',
      '/team-player-report-cards': 'team-player-report-cards',
      '/team-lineups': 'team-lineups',
      '/team-lineup-editor': 'team-lineup-editor',
      '/team-work-list': 'team-work-list',
      '/team-schedule': 'team-schedule'
    };

    return staticRoutes[pathname] || null;
  };

  // Sync URL changes to view state
  useEffect(() => {
    // Skip navigation sync for special URLs that are handled directly in App.js routing
    if (location.pathname.match(/^\/match\/[a-zA-Z0-9]+$/)) {
      return;
    }
    if (location.pathname === '/team-player-reports') {
      return;
    }
    // Skip if view is null (used for special routes)
    if (view === null) {
      return;
    }

    const newView = getViewFromPath(location.pathname);
    if (newView && newView !== view) {
      setView(newView);
    }
  }, [location.pathname]); // Intentionally minimal deps to avoid loops

  // Sync view changes to URL
  useEffect(() => {
    // Skip navigation sync for special URLs that are handled directly in App.js routing
    if (location.pathname.match(/^\/match\/[a-zA-Z0-9]+$/)) {
      return;
    }
    if (location.pathname === '/team-player-reports') {
      return;
    }
    // Skip if view is null (used for special routes)
    if (view === null) {
      return;
    }

    const newPath = getPathForView(view);
    if (newPath && location.pathname !== newPath) {
      navigate(newPath, { replace: true });
    }
  }, [view, activePlayerId, activeSeasonId, activeTeamId]); // Update URL when IDs change

  // Return navigate function for direct URL navigation
  return navigate;
}
